import { syncNow } from './api'
import { addTask, db } from './db'

interface ModelContextTool {
  name: string
  title: string
  description: string
  inputSchema: Record<string, unknown>
  annotations: { readOnlyHint: boolean; untrustedContentHint: boolean }
  execute(input: unknown): unknown | Promise<unknown>
}

interface ModelContext {
  registerTool(tool: ModelContextTool, options?: { signal?: AbortSignal }): void | Promise<void>
}

declare global {
  interface Document {
    readonly modelContext?: ModelContext
  }
}

function asRecord(input: unknown): Record<string, unknown> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Input must be an object.')
  return input as Record<string, unknown>
}

export function registerTaskTools() {
  const context = document.modelContext
  if (!context?.registerTool) return () => undefined
  const lifecycle = new AbortController()

  const registrations = [
    context.registerTool(
      {
        name: 'list_open_tasks',
        title: 'List open tasks',
        description: 'List the current incomplete tasks and their category names without changing anything.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        async execute() {
          const [tasks, categories] = await Promise.all([
            db.tasks.filter((task) => !task.deletedAt && !task.completed).toArray(),
            db.categories.filter((category) => !category.deletedAt).toArray(),
          ])
          const names = new Map(categories.map((category) => [category.id, category.name]))
          return {
            count: tasks.length,
            tasks: tasks.map((task) => ({ id: task.id, title: task.title, details: task.details, category: names.get(task.categoryId) || 'Unknown' })),
          }
        },
      },
      { signal: lifecycle.signal },
    ),
    context.registerTool(
      {
        name: 'add_task',
        title: 'Add a task',
        description: 'Create one task in an existing category. Uses Inbox when no category is supplied.',
        inputSchema: {
          type: 'object',
          properties: {
            title: { type: 'string', minLength: 1, maxLength: 500 },
            details: { type: 'string', maxLength: 20000 },
            category: { type: 'string', description: 'Existing category name' },
          },
          required: ['title'],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        async execute(input) {
          const value = asRecord(input)
          const title = typeof value.title === 'string' ? value.title.trim() : ''
          const details = typeof value.details === 'string' ? value.details : ''
          const categoryName = typeof value.category === 'string' ? value.category.trim().toLocaleLowerCase() : 'inbox'
          if (!title || title.length > 500) throw new Error('Title must be between 1 and 500 characters.')
          if (details.length > 20000) throw new Error('Details are too long.')
          const categories = await db.categories.filter((category) => !category.deletedAt).toArray()
          const category = categories.find((item) => item.name.toLocaleLowerCase() === categoryName) || categories[0]
          if (!category) throw new Error('Create a category before adding a task.')
          const task = await addTask(category.id, title, details)
          void syncNow().catch(() => undefined)
          return { id: task.id, title: task.title, category: category.name, saved: true }
        },
      },
      { signal: lifecycle.signal },
    ),
  ]

  for (const registration of registrations) void Promise.resolve(registration).catch(() => undefined)
  return () => lifecycle.abort()
}

